// src/components/FormLists/FormListsGroupHeaderContent.jsx

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Tooltip,
  Typography,
} from '@mui/material';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import BusinessIcon from '@mui/icons-material/Business';

import {
  normalizeString,
} from '../Spreadsheet/groupedSpreadsheetUtils';

/**
 * FormListsGroupHeaderContent
 * -----------------------------------------------------------------------------
 * Shared Form Lists header/row label content for spreadsheet group labels and
 * side-panel list rows.
 *
 * PURPOSE
 *  • Render the Form Lists field identity in one reusable place:
 *      field type icon + field label + optional SmartList indicator
 *  • Keep the shared Spreadsheet/ engine domain-free.
 *  • Keep FormListsLabSidePanelList and GroupedSpreadsheetSheet visually aligned.
 *
 * ICON RULES
 *  • single-select -> ArrowDropDownCircleIcon
 *  • multi-select  -> CheckBoxIcon
 *  • org-managed / SmartList-backed fields also show the org-wide icon after
 *    the label with a "SmartList" tooltip.
 *
 * IMPORTANT
 *  • Icon color intentionally inherits from the surrounding label text.
 *  • This lets the parent decide whether the label appears as text.primary,
 *    text.secondary, selected, disabled, etc.
 *  • This component does NOT read/write context, storage, or Firestore.
 *  • This component does NOT know about Spreadsheet layout mechanics.
 * -----------------------------------------------------------------------------
 */

const SMARTLIST_TOOLTIP_LABEL = 'SmartList';

const VARIANT_CONFIG = Object.freeze({
  sheet: {
    typographyVariant: 'caption',
    fontSize: '11px',
    fontWeight: 700,
    lineHeight: 1.2,
    iconSize: '0.95rem',
    gap: 0.75,
  },
  sidepanel: {
    typographyVariant: 'body2',
    fontSize: '0.8rem',
    fontWeight: 500,
    lineHeight: 1.35,
    iconSize: '1rem',
    gap: 1,
  },
});

const getFieldType = (group = {}) => (
  normalizeString(group?.fieldType, '') ||
  normalizeString(group?.fieldMetadata?.type, '') ||
  normalizeString(group?.field?.type, '') ||
  normalizeString(group?.type, '')
);

const getFieldLabel = ({
  group = {},
  label = '',
} = {}) => (
  normalizeString(label, '') ||
  normalizeString(group?.title, '') ||
  normalizeString(group?.fieldMetadata?.label, '') ||
  normalizeString(group?.field?.label, '') ||
  normalizeString(group?.label, '') ||
  'Untitled field'
);

const isSmartListBackedGroup = (group = {}) => (
  group?.isOrgManaged === true ||
  Boolean(
    normalizeString(group?.managedFieldId, '') ||
      normalizeString(group?.managedOptionSetId, '') ||
      normalizeString(group?.fieldMetadata?.managedFieldId, '') ||
      normalizeString(group?.fieldMetadata?.managedOptionSetId, '')
  )
);

const getFieldTypeTooltip = (fieldType = '') => {
  const normalized = normalizeString(fieldType, '');

  if (normalized === 'multi-select') return 'multi-select';
  if (normalized === 'single-select') return 'single-select';

  return normalized || 'dropdown field';
};

const getFieldTypeIcon = (fieldType = '') => {
  if (normalizeString(fieldType, '') === 'multi-select') {
    return CheckBoxIcon;
  }

  return ArrowDropDownCircleIcon;
};

const FormListsGroupHeaderContent = memo(({
  group,
  label = '',
  variant = 'sheet',
  showSmartListIndicator = true,
}) => {
  const fieldType = getFieldType(group);
  const fieldLabel = getFieldLabel({
    group,
    label,
  });

  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.sheet;
  const FieldTypeIcon = getFieldTypeIcon(fieldType);
  const fieldTypeTooltip = getFieldTypeTooltip(fieldType);
  const showOrgWideIcon =
    showSmartListIndicator === true &&
    isSmartListBackedGroup(group);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        minWidth: 0,
        maxWidth: '100%',
        gap: config.gap,
        color: 'inherit',
      }}
    >
      <Tooltip
        title={fieldTypeTooltip}
        placement="top"
        arrow
      >
        <Box
          component="span"
          aria-label={fieldTypeTooltip}
          sx={{
            flex: '0 0 auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 0,
            color: 'inherit',
            lineHeight: 0,
          }}
        >
          <FieldTypeIcon
            sx={{
              fontSize: config.iconSize,
              color: 'inherit',
            }}
          />
        </Box>
      </Tooltip>

      <Tooltip
        title={fieldLabel}
        placement="top"
        arrow
      >
        <Typography
          variant={config.typographyVariant}
          noWrap
          sx={{
            flex: '0 1 auto',
            minWidth: 0,
            maxWidth: '100%',
            fontSize: config.fontSize,
            fontWeight: config.fontWeight,
            lineHeight: config.lineHeight,
            color: 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fieldLabel}
        </Typography>
      </Tooltip>

      {showOrgWideIcon ? (
        <Tooltip
          title={SMARTLIST_TOOLTIP_LABEL}
          placement="top"
          arrow
        >
          <Box
            component="span"
            aria-label={SMARTLIST_TOOLTIP_LABEL}
            sx={{
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 0,
              color: 'inherit',
              lineHeight: 0,
            }}
          >
            <BusinessIcon
              sx={{
                fontSize: config.iconSize,
                color: 'inherit',
              }}
            />
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );
});

FormListsGroupHeaderContent.displayName = 'FormListsGroupHeaderContent';

FormListsGroupHeaderContent.propTypes = {
  group: PropTypes.object.isRequired,
  label: PropTypes.string,
  variant: PropTypes.oneOf(['sheet', 'sidepanel']),
  showSmartListIndicator: PropTypes.bool,
};

export default FormListsGroupHeaderContent;